import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { translations, DEFAULT_LANG, SUPPORTED } from './translations';

const STORAGE_KEY = 'jbss-lang';

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  toggleLang: () => {},
  t: (path) => path,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANG;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
    return DEFAULT_LANG;
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
      document.documentElement.setAttribute('data-lang', lang);
    }
  }, [lang]);

  const setLang = useCallback((newLang) => {
    if (SUPPORTED.includes(newLang)) setLangState(newLang);
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((prev) => (prev === 'kn' ? 'en' : 'kn'));
  }, []);

  // dot-path translator: t('hero.title')
  const t = useCallback(
    (path, vars) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      const parts = path.split('.');
      let value = dict;
      for (const p of parts) {
        if (value && typeof value === 'object' && p in value) {
          value = value[p];
        } else {
          value = path;
          break;
        }
      }
      if (typeof value === 'string' && vars) {
        return value.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : `{${k}}`));
      }
      return value;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggleLang, t }), [lang, setLang, toggleLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
